import React, { createContext, useContext, useReducer } from "react";

const RegistrationContext = createContext(null);
const RegistrationDispatchContext = createContext(null);

const initialState = {
  // From URL params
  productFamily: "",
  licenseType: "",
  pakId: "",
  isBoxx: false,
  partner: "",

  // From GET /api/ucj/initialize
  masterData: null,
  rwaMappings: [],
  ngcCreateRequired: false,
  ngcProductFamily: "",

  // From GET /api/ucj/user-profile
  userProfile: null,       // null until loaded; { exists: bool, ...fields }

  // Stepper
  // Possible values: 'tenant' | 'form' | 'progress'
  currentStep: "form",
  steps: [],               // ordered list of step keys for this flow

  // Form data — pre-populated from userProfile if returning user
  form: {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    jobRoleId: 0,
    companyName: "",
    countryId: 0,
    stateId: null,
    city: "",
    zipCode: "",
    addressLine1: "",
    addressLine2: "",
    industryId: 0,
    claimingEntitlementAs: 0,
    partnerReferral: "",
    agreeToRegisterTerms: false,
  },

  // Tenant / NCA data (set after Tenant Selector completes)
  tenant: {
    nca_nan: "",
    nca_nid: "",
    starfleet_id: "",
    idp_id: "",
    accountName: "",
    primaryOwnerEmail: "",
    showPicker: false,
    ngcProductNames: "",
    sessionId: "",
  },

  // Post-submit
  registrationId: null,
  appHubUrl: "",

  // UI state
  isInitializing: true,
  isLoadingProfile: false,
  isSubmitting: false,
  submitError: null,
};

function registrationReducer(state, action) {
  switch (action.type) {
    case "SET_URL_PARAMS":
      return { ...state, ...action.payload };

    case "INITIALIZE_SUCCESS":
      return {
        ...state,
        masterData: action.payload.masterData,
        rwaMappings: action.payload.rwaMappings,
        ngcCreateRequired: action.payload.ngcCreateRequired,
        ngcProductFamily: action.payload.ngcProductFamily,
        isInitializing: false,
      };

    case "INITIALIZE_ERROR":
      return { ...state, isInitializing: false, submitError: action.payload };

    case "SET_LOADING_PROFILE":
      return { ...state, isLoadingProfile: action.payload };

    case "USER_PROFILE_LOADED": {
      const profile = action.payload;
      const form = profile.exists
        ? {
            ...state.form,
            firstName: profile.firstName || "",
            lastName: profile.lastName || "",
            email: profile.email || state.form.email,
            phone: profile.phone || "",
            jobRoleId: profile.jobRoleId || 0,
            companyName: profile.companyName || "",
            countryId: profile.countryId || 0,
            stateId: profile.stateId || null,
            city: profile.city || "",
            zipCode: profile.zipCode || "",
            addressLine1: profile.addressLine1 || "",
            addressLine2: profile.addressLine2 || "",
            industryId: profile.industryId || 0,
          }
        : state.form;
      return { ...state, userProfile: profile, form, isLoadingProfile: false };
    }

    case "SET_STEPS":
      return { ...state, steps: action.payload, currentStep: action.payload[0] };

    case "NEXT_STEP": {
      const idx = state.steps.indexOf(state.currentStep);
      const next = state.steps[idx + 1];
      return next ? { ...state, currentStep: next } : state;
    }

    case "PREV_STEP": {
      const idx = state.steps.indexOf(state.currentStep);
      const prev = state.steps[idx - 1];
      return prev ? { ...state, currentStep: prev } : state;
    }

    case "UPDATE_FORM":
      return { ...state, form: { ...state.form, ...action.payload } };

    case "TENANT_COMPLETE":
      return {
        ...state,
        tenant: { ...state.tenant, ...action.payload },
      };

    case "SET_EMAIL":
      return { ...state, form: { ...state.form, email: action.payload } };

    case "SUBMIT_START":
      return { ...state, isSubmitting: true, submitError: null };

    case "SUBMIT_SUCCESS":
      return {
        ...state,
        isSubmitting: false,
        registrationId: action.payload.registrationId,
      };

    case "SUBMIT_ERROR":
      return { ...state, isSubmitting: false, submitError: action.payload };

    case "SET_APPHUB_URL":
      return { ...state, appHubUrl: action.payload };

    default:
      return state;
  }
}

export function RegistrationProvider({ children }) {
  const [state, dispatch] = useReducer(registrationReducer, initialState);
  return (
    <RegistrationContext.Provider value={state}>
      <RegistrationDispatchContext.Provider value={dispatch}>
        {children}
      </RegistrationDispatchContext.Provider>
    </RegistrationContext.Provider>
  );
}

export function useRegistration() {
  return useContext(RegistrationContext);
}

export function useRegistrationDispatch() {
  return useContext(RegistrationDispatchContext);
}
